import React, { useMemo, useRef, useState } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
library.add(fas)

import { getRequest, postRequest, patchRequest, csrfToken, pathname } from "@react/helpers/fetch"
import { roundMoney } from "@react/helpers/helpers"
import Header      from "./components/Header"
import LinesTable  from "./components/LinesTable"
import SummaryPane from "./components/SummaryPane"

let uidCounter = 0
const nextUid = () => `line-${uidCounter += 1}`

const ANSWER_KEY = {
  string:   "string_answer",
  options:  "string_answer",
  link_url: "string_answer",
  date:     "date_answer",
  integer:  "integer_answer",
  float:    "float_answer",
  boolean:  "boolean_answer",
}

const withUid = line => ({
  description:         "",
  quantity:            1,
  unit:                "",
  unit_price:          0,
  discount:            0,
  vat:                 0,
  vat_rate_id:         null,
  billing_category_id: null,
  qline_id:            null,
  is_page_break:       false,
  is_descriptive:      false,
  ...line,
  _uid: nextUid(),
})

const clampDiscount = value => Math.min(100, Math.max(0, parseFloat(value) || 0))

const computeTotals = (lines, discountPct = 0) => {
  let grossCents = 0
  const vatGroups = {}
  lines.forEach(line => {
    if (line.is_page_break) return
    const quantity  = parseFloat(line.quantity)  || 0
    const unitPrice = parseFloat(line.unit_price) || 0
    const discount  = parseFloat(line.discount)   || 0
    const ht = roundMoney(quantity * unitPrice * (1 - discount / 100))
    grossCents += Math.round(ht * 100)
    const pct = parseFloat(line.vat) || 0
    vatGroups[pct] = (vatGroups[pct] || 0) + ht
  })
  // The global discount applies to every line, VAT included, as Vat::GetHash does.
  const discount      = clampDiscount(discountPct)
  const discountCents = Math.round(grossCents * discount / 100)
  const htCents       = grossCents - discountCents
  const vatBreakdown = Object.entries(vatGroups)
    .map(([pct, base]) => ({ pct: parseFloat(pct), amountCents: Math.round(roundMoney(base * parseFloat(pct) * (100 - discount) / 10000) * 100) }))
    .filter(entry => entry.pct > 0)
    .sort((first, second) => second.pct - first.pct)
  const vatCents = vatBreakdown.reduce((accumulator, entry) => accumulator + entry.amountCents, 0)
  return { grossCents, discountCents, htCents, vatCents, ttcCents: htCents + vatCents, vatBreakdown }
}

const App = ({ data, save_url, save_method, preview_url, back_route }) => {
  const vatRates      = data.vat_rates || []
  const [defaultVatRateId, setDefaultVatRateId] = useState(data.default_vat_rate_id)
  const defaultVat    = vatRates.find(rate => String(rate.id) === String(defaultVatRateId))
  const defaultVatPct = defaultVat ? Number(defaultVat.percentage) : 0

  const [header, setHeader] = useState({
    title:               data.title || "",
    description:         data.description || "",
    internal_info:       data.internal_info || "",
    client_reference:    data.client_reference || "",
    creation_date:       data.creation_date || "",
    payment_delay:       data.payment_delay ?? "",
    end_of_month:        !!data.end_of_month,
    discount:            data.discount ?? 0,
    deposit:             !!data.deposit,
    billing_category_id: data.billing_category_id || "",
    category_id:         data.category_id || "",
    display_int_address:        !!data.display_int_address,
    reminders_enabled:          !!data.reminders_enabled,
    should_enable_payment_link: !!data.should_enable_payment_link,
  })
  const [client, setClient]         = useState(data.client || null)
  const [lines, setLines]           = useState(() => (data.lines || []).map(withUid))
  const [deletedLineIds, setDeletedLineIds] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [propertyValues, setPropertyValues] = useState(() => {
    const initial = {}
    ;(data.property_fields || []).forEach(field => {
      if (field.value != null) initial[field.property_field_blueprint_id] = field.value
    })
    return initial
  })

  const updateHeader   = patch => setHeader(previous => ({ ...previous, ...patch }))

  const [site, setSite]   = useState(data.site || null)
  const [billingCategories, setBillingCategories] = useState(data.billing_categories || [])
  const clientSitesRef    = useRef({})
  const pickedClientRef   = useRef(data.client?.id)
  const pickedSiteRef     = useRef(data.site?.id)

  const loadClient = id => {
    if (clientSitesRef.current[id]) return Promise.resolve(clientSitesRef.current[id])
    return getRequest(pathname({ path: `/clients/${id}/build`, format: "json", params: { skip_contacts: true } }))
      .then(response => (clientSitesRef.current[id] = response || {}))
  }

  const searchClients = term =>
    getRequest(pathname({ path: "/clients/autocomplete", format: "json", params: term ? { q: term } : {} }))
      .then(found => (Array.isArray(found) ? found : []))

  const searchSites = term => {
    if (!client?.id) return []
    const needle = (term || "").toLowerCase()
    return loadClient(client.id).then(response => (response.sites || []).filter(entry => (entry.name || "").toLowerCase().includes(needle)))
  }

  const changeSite = picked => {
    if (picked.id === pickedSiteRef.current) return
    pickedSiteRef.current = picked.id
    setSite({ id: picked.id, name: picked.name })
    getRequest(pathname({ path: `/sites/${picked.id}/build`, format: "json", params: { skip_contacts: true, skip_materials: true } }))
      .then(response => {
        if (pickedSiteRef.current !== picked.id) return
        const rate = vatRates.find(entry => String(entry.id) === String(response?.vat_rate?.id))
        if (!rate) return
        setDefaultVatRateId(rate.id)
        setLines(previous => previous.map(line => (
          line.is_page_break || line.is_descriptive ? line : { ...line, vat_rate_id: rate.id, vat: Number(rate.percentage) }
        )))
      })
  }

  const changeClient = picked => {
    if (picked.id === pickedClientRef.current) return
    pickedClientRef.current = picked.id
    pickedSiteRef.current   = null
    setClient({ id: picked.id, name: picked.name })
    setSite(null)
    loadClient(picked.id).then(response => {
      if (pickedClientRef.current !== picked.id) return
      const sites = response.sites || []
      if (sites.length === 1) changeSite(sites[0])
      const categories = response.billing_categories || []
      const known      = id => !id || categories.some(category => String(category.id) === String(id))
      if (categories.length > 0) {
        setBillingCategories(categories)
        setLines(previous => previous.map(line => (known(line.billing_category_id) ? line : { ...line, billing_category_id: null })))
      }
      setHeader(previous => ({
        ...previous,
        payment_delay:              response.payment_delay ?? "",
        end_of_month:               !!response.end_of_month,
        reminders_enabled:          !!response.invoice_reminders_enabled_by_default,
        should_enable_payment_link: !!response.online_payment_enabled_by_default,
        ...(categories.length > 0 && !known(previous.billing_category_id) ? { billing_category_id: "" } : {}),
      }))
    })
  }

  const updateProperty = (blueprintId, value) => setPropertyValues(previous => ({ ...previous, [blueprintId]: value }))

  const updateLine = (uid, patch) =>
    setLines(previous => previous.map(line => (line._uid === uid ? { ...line, ...patch } : line)))

  const setLineVat = (uid, vatRateId) => {
    const rate = vatRates.find(entry => String(entry.id) === String(vatRateId))
    updateLine(uid, { vat_rate_id: rate ? rate.id : null, vat: rate ? Number(rate.percentage) : 0 })
  }

  const addLine = (overrides = {}) => {
    const plain = overrides.is_page_break || overrides.is_descriptive
    setLines(previous => [...previous, withUid({
      vat_rate_id: plain ? null : defaultVatRateId,
      vat:         plain ? 0 : defaultVatPct,
      ...overrides,
    })])
  }

  const removeLine = uid => setLines(previous => {
    const removed = previous.find(line => line._uid === uid)
    if (removed?.id) setDeletedLineIds(ids => [...ids, removed.id])
    return previous.filter(line => line._uid !== uid)
  })

  const moveLine = (uid, direction) => setLines(previous => {
    const index = previous.findIndex(line => line._uid === uid)
    const target = index + direction
    if (index < 0 || target < 0 || target >= previous.length) return previous
    const next = [...previous]
    ;[next[index], next[target]] = [next[target], next[index]]
    return next
  })

  const totals = useMemo(() => computeTotals(lines, header.discount), [lines, header.discount])

  // Dirty tracking (like the quote builder): Save is enabled only when the
  // form differs from what was loaded.
  const currentContent    = JSON.stringify({ header, client, site, lines: lines.map(({ _uid, ...rest }) => rest), propertyValues })
  const initialContentRef = useRef(currentContent)
  const dirty             = currentContent !== initialContentRef.current
  const isEdit            = save_method === "patch"

  const buildBody = commit => ({
    commit,
    invoice: {
      title:               header.title,
      description:         header.description,
      internal_info:       header.internal_info,
      client_reference:    header.client_reference,
      creation_date:       header.creation_date,
      payment_delay:       header.payment_delay,
      end_of_month:        header.end_of_month,
      discount:            clampDiscount(header.discount),
      deposit:             header.deposit,
      billing_category_id: header.billing_category_id || null,
      category_id:         header.category_id || null,
      display_int_address:        header.display_int_address,
      reminders_enabled:          header.reminders_enabled,
      should_enable_payment_link: header.should_enable_payment_link,
      client_id:           client?.id,
      site_id:             site?.id,
      invoice_lines_attributes: [
        ...lines.map((line, index) => {
          const base = { id: line.id, description: line.description, is_page_break: line.is_page_break, order: index + 1 }
          if (line.is_page_break)  return base
          if (line.is_descriptive) return { ...base, quantity: null, unit_price: 0 }
          return {
            ...base,
            quantity:            line.quantity,
            unit:                line.unit,
            unit_price:          line.unit_price,
            discount:            line.discount,
            vat_rate_id:         line.vat_rate_id || null,
            billing_category_id: line.billing_category_id || null,
          }
        }),
        ...deletedLineIds.map(id => ({ id, _destroy: true })),
      ],
      property_fields_attributes: (data.property_fields || [])
        .filter(field => field.field_type !== "sub_title")
        .map(field => ({
          id:                          field.property_field_id,
          property_field_blueprint_id: field.property_field_blueprint_id,
          field_type:                  field.field_type,
          title:                       field.title,
          required:                    field.required,
          order:                       field.order,
          special_function:            field.special_function,
          [ANSWER_KEY[field.field_type] || "string_answer"]: propertyValues[field.property_field_blueprint_id],
        })),
    },
  })

  const submit = commit => {
    if (submitting) return
    if (lines.length === 0) {
      flashAlert(I18n.t("projects.invoices.errors.no_lines"))
      return
    }
    if (!client?.id || !site?.id) {
      flashAlert(I18n.t("projects.invoices.errors.site_required"))
      return
    }
    if (data.mandatory_category && data.categories?.length > 0 && !header.category_id) {
      flashAlert(I18n.t("projects.invoices.errors.category_required"))
      return
    }
    setSubmitting(true)
    const request = save_method === "patch" ? patchRequest : postRequest
    request({ url: save_url, body: buildBody(commit) })
      .then(response => {
        if (!response?.success) {
          flashAlert(response?.errors ? (Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors)) : I18n.t("quotes.builder.footer.unknown_error"))
          setSubmitting(false)
          return
        }
        if (response.redirect_to) window.location.href = response.redirect_to
        else setSubmitting(false)
      })
      .catch(() => {
        flashAlert(I18n.t("quotes.builder.footer.network_error"))
        setSubmitting(false)
      })
  }

  const preview = () => {
    if (previewing || submitting) return
    setPreviewing(true)
    fetch(preview_url, {
      method:      "POST",
      credentials: "same-origin",
      headers:     { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body:        JSON.stringify(buildBody("preview")),
    })
      .then(response => response.blob())
      .then(blob => {
        window.open(URL.createObjectURL(blob), "_blank")
        setPreviewing(false)
      })
      .catch(() => {
        flashAlert(I18n.t("quotes.builder.footer.network_error"))
        setPreviewing(false)
      })
  }

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <Header
        project        = {data.project}
        client         = {client}
        onClientChange = {changeClient}
        searchClients  = {searchClients}
        site           = {site}
        onSiteChange   = {changeSite}
        searchSites    = {searchSites}
        backRoute      = {back_route}
        header         = {header}
        onHeaderChange = {updateHeader}
        heading                  = {data.heading}
        minDate                  = {data.min_date}
        billingCategories        = {billingCategories}
        billingCategoryOnLine    = {data.billing_category_on_invoice_line}
        billingCategoryMandatory = {data.billing_category_mandatory}
        categoriesEnabled        = {data.categories_enabled}
        categories               = {data.categories || []}
        categoryMandatory        = {data.mandatory_category}
        remindersAvailable       = {data.reminders_available}
        onlinePaymentAvailable   = {data.online_payment_available}
        propertyFields           = {data.property_fields || []}
        propertyValues           = {propertyValues}
        onPropertyChange         = {updateProperty}
      />

      <div className="px-8 py-6 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6 items-start">
        <LinesTable
          lines               = {lines}
          vatRates            = {vatRates}
          billingCategories   = {billingCategories}
          showBillingCategory = {data.billing_category_on_invoice_line}
          currencySymbol      = {data.currency_symbol}
          onLineChange        = {updateLine}
          onLineVatChange     = {setLineVat}
          onAddLine           = {addLine}
          onRemoveLine        = {removeLine}
          onMoveLine          = {moveLine}
        />

        <SummaryPane
          totals         = {totals}
          discount       = {header.discount}
          onDiscountChange = {value => updateHeader({ discount: value })}
          currencySymbol = {data.currency_symbol}
          submitting     = {submitting}
          previewing     = {previewing}
          dirty          = {dirty}
          isEdit         = {isEdit}
          generated      = {data.generated}
          onPreview      = {preview}
          onCreate       = {() => submit("save")}
          onGenerate     = {() => submit("generate")}
        />
      </div>

      {(submitting || previewing) && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl bg-white border border-slate-200 shadow-lg">
            <FontAwesomeIcon icon="spinner" spin className="text-blue-600 text-lg" />
            <span className="text-sm font-medium text-slate-900">
              {previewing
                ? I18n.t("projects.invoices.builder.generating_pdf")
                : I18n.t("projects.invoices.builder.saving")}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default App
